import React, { useState, useEffect, useRef } from 'react';
import { Block } from '../classes/Block';
import type { RowData } from '../App';
import './BlockConfigForm.css';
import { t } from '@lingui/core/macro';
import {
  Select,
  MenuItem,
  Collapse,
} from "@mui/material";
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignVerticalTopIcon from '@mui/icons-material/AlignVerticalTop';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';
import FormatTextdirectionLToRIcon from '@mui/icons-material/FormatTextdirectionLToR';
import FormatTextdirectionRToLIcon from '@mui/icons-material/FormatTextdirectionRToL';
import HdrAutoIcon from '@mui/icons-material/HdrAuto';
import ColorPicker from './ColorPicker';
import CopyFormattingDialog, { FormattingGroup } from './CopyFormattingDialog';
import ReusableFontPicker, { FontFamilyOption, FontOption } from './ReusableFontPicker';
import FontWeightWidthControls from './FontWeightWidthControls';
import IconToggleGroup from './IconToggleGroup';
import {
  BlockNamePlacementHorizontalLeftIcon,
  BlockNamePlacementHorizontalRightIcon,
  BlockNamePlacementVerticalIcon,
} from './BlockNamePlacementIcons';
import type { CardGroup } from '../utils/cardGroups';
import { useProject } from '../contexts/ProjectContext';
import { trackEvent } from '../statsig';
import {
  VariableFontConfig,
  normalizeVariableFontConfigSteps,
} from '../utils/fontHelpers';

const COPY_LAYOUT_FORMATTING_FIELDS: ReadonlyArray<keyof Block> = [
  'cardVerticalCenter',
  'cardOverrideFullWidth',
  'cardHorizontalOffsetPx',
  'isSongBlock',
  'isSingleCell',
  'isCreditsList',
  'isLogoBlock',
  'shouldDisplayBlockName',
  'blockNameAlignment',
  'blockNamePlacement',
  'blockNameFont',
  'blockNameSize',
  'blockNameWeight',
  'blockNameFontWidth',
  'blockNameColor',
  'numColumns',
  'sbsBlocks',
  'sbsDistance',
  'distanceBelowBlockName',
  'exchangeLabelValue',
  'labelValueGap',
  'labelValueAlignment',
  'ltrRtlControl',
  'marginColumn',
  'distanceBetweenRows',
  'edgeDistance',
  'distanceFromPreviousBlock',
  'separator',
];

const COPY_LABEL_FORMATTING_FIELDS: ReadonlyArray<keyof Block> = [
  'labelFontName',
  'labelFontSize',
  'labelFontWeight',
  'labelFontWidth',
  'labelAlign',
  'labelVerticalAlign',
  'labelFontColor',
];

const COPY_VALUE_FORMATTING_FIELDS: ReadonlyArray<keyof Block> = [
  'hasSeparateValueFormatting',
  'valueFontName',
  'valueFontSize',
  'valueFontWeight',
  'valueFontWidth',
  'valueAlign',
  'valueVerticalAlign',
  'valueFontColor',
];

const COPY_FORMATTING_FIELDS: Record<FormattingGroup, ReadonlyArray<keyof Block>> = {
  layout: COPY_LAYOUT_FORMATTING_FIELDS,
  label: COPY_LABEL_FORMATTING_FIELDS,
  value: COPY_VALUE_FORMATTING_FIELDS,
  all: [
    ...COPY_LAYOUT_FORMATTING_FIELDS,
    ...COPY_LABEL_FORMATTING_FIELDS,
    ...COPY_VALUE_FORMATTING_FIELDS,
  ],
};

const pickBlockFields = <K extends keyof Block>(source: Block, keys: readonly K[]): Pick<Block, K> => {
  const result: Pick<Block, K> = {} as Pick<Block, K>;
  keys.forEach((key: K) => {
    result[key] = source[key];
  });
  return result;
};

interface BlockConfigFormProps {
  blockNames: string[]; // These are potential names for NEW blocks, from App.tsx's firstColValues
  onSaveBlocks: (blocks: Block[]) => void | Promise<void>;
  onBlockSelect?: (index: number) => void;
  selectedBlockIndex?: number | null;
  existingBlocks: Block[];
  fontFamilies: FontFamilyOption[];
  isCardsMode: boolean;
  cardGroups: CardGroup[];
}

interface LogoRowConfig {
  scale: number;
  xOffset: number;
  yOffset: number;
  autoCrop: boolean;
}

type LogoNumericConfigField = Exclude<keyof LogoRowConfig, 'autoCrop'>;

const DEFAULT_LOGO_ROW_CONFIG: Readonly<LogoRowConfig> = {
  scale: 100,
  xOffset: 0,
  yOffset: 0,
  autoCrop: false,
};

const getLogoRowConfig = (row: RowData | undefined): LogoRowConfig => {
  const serializedConfig: unknown = row?.col2;
  if (typeof serializedConfig !== 'string') {
    return { ...DEFAULT_LOGO_ROW_CONFIG };
  }

  try {
    const parsedConfig: Partial<LogoRowConfig> = JSON.parse(serializedConfig) as Partial<LogoRowConfig>;
    return {
      scale: typeof parsedConfig.scale === 'number' ? parsedConfig.scale : DEFAULT_LOGO_ROW_CONFIG.scale,
      xOffset: typeof parsedConfig.xOffset === 'number' ? parsedConfig.xOffset : DEFAULT_LOGO_ROW_CONFIG.xOffset,
      yOffset: typeof parsedConfig.yOffset === 'number' ? parsedConfig.yOffset : DEFAULT_LOGO_ROW_CONFIG.yOffset,
      autoCrop: typeof parsedConfig.autoCrop === 'boolean' ? parsedConfig.autoCrop : DEFAULT_LOGO_ROW_CONFIG.autoCrop,
    };
  } catch {
    return { ...DEFAULT_LOGO_ROW_CONFIG };
  }
};

const BlockConfigForm: React.FC<BlockConfigFormProps> = ({
  blockNames,
  onSaveBlocks,
  onBlockSelect,
  selectedBlockIndex,
  existingBlocks,
  fontFamilies,
  isCardsMode,
  cardGroups
}) => {
  const { selectedProject } = useProject();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [activeBlockIndex, setActiveBlockIndex] = useState<number>(0);
  const [selectedLogoRowIndex, setSelectedLogoRowIndex] = useState<number>(0);
  type SettingsTab = 'layout' | 'font';
  const [settingsTabs, setSettingsTabs] = useState<Record<'block' | 'card' | 'label' | 'value', SettingsTab>>({
    block: 'layout',
    card: 'layout',
    label: 'layout',
    value: 'layout',
  });
  const [collapsedSettingsGroups, setCollapsedSettingsGroups] = useState<Record<'block' | 'card' | 'label' | 'value' | 'logo', boolean>>({
    block: false,
    label: true,
    logo: false,
    value: true,
    card: false,
  });
  const [copyFormattingGroup, setCopyFormattingGroup] = useState<FormattingGroup | null>(null);
  const [copyTargetBlockIds, setCopyTargetBlockIds] = useState<Set<string>>(new Set());

  // Reference to track if blocks were updated from props to prevent feedback loops
  const isUpdatingFromProps = useRef<boolean>(false);

  // Synchronize the internal `blocks` state with the `existingBlocks` prop.
  // This is the primary way blocks (with dataRows) enter the form.
  useEffect(() => {
    console.log('BlockConfigForm: existingBlocks prop received:', existingBlocks);
    const normalizedBlocks: Block[] = existingBlocks.map((block: Block) => {
      const isPlainSingleCellBlockValue: boolean =
        block.isSingleCell && !block.isSongBlock && !block.isCreditsList && !block.isLogoBlock;
      return Block.create({ // Create new instances to ensure no shared refs with App.tsx state directly
        ...block,
        hasSeparateValueFormatting: isPlainSingleCellBlockValue ? true : block.hasSeparateValueFormatting,
        dataRows: block.dataRows || []
      });
    });

    setBlocks((previousBlocks: Block[]) => {
      if (JSON.stringify(previousBlocks) === JSON.stringify(normalizedBlocks)) {
        console.log('BlockConfigForm: Internal blocks seem identical to prop, no update.');
        return previousBlocks;
      }

      console.log('BlockConfigForm: Internal blocks are different from prop, updating internal blocks.');
      isUpdatingFromProps.current = true; // Mark that we're updating from props
      return normalizedBlocks;
    });
    // If existingBlocks is empty, and we have blockNames, this might be an initial setup from App.tsx
    // where App.tsx hasn't yet processed an actual file but wants to show block tabs based on some default names.
    // However, App.tsx is now primarily responsible for creating initial blocks even from just names.
  }, [existingBlocks]);

  // useEffect to automatically save blocks whenever they change internally
  useEffect(() => {
    // Skip saving if the update came from props to prevent feedback loop
    if (isUpdatingFromProps.current) {
      console.log('BlockConfigForm: Skipping save because update came from props');
      isUpdatingFromProps.current = false; // Reset the flag
      return;
    }

    // Only call onSaveBlocks if the blocks array reference itself has changed
    // and it's not an initial setup or an update directly from props.
    // The comparison `JSON.stringify(blocks) !== JSON.stringify(existingBlocks)` helps ensure we save
    // when internal changes make `blocks` different from the last `existingBlocks` received.
    if (JSON.stringify(blocks) !== JSON.stringify(existingBlocks)) {
      // Further check: Ensure that the current `blocks` state is not empty if `existingBlocks` wasn't, 
      // to prevent saving an accidentally cleared state. This is a safeguard.
      if (blocks.length > 0 || (existingBlocks && existingBlocks.length === 0)) {
        console.log('BlockConfigForm: Internal blocks changed, calling onSaveBlocks with:', blocks);
        const result = onSaveBlocks(blocks);
        if (result instanceof Promise) {
          result.catch(error => {
            console.error('Error in onSaveBlocks:', error);
          });
        }
      } else {
        console.log('BlockConfigForm: Internal blocks changed but are empty while existingBlocks were not; suppressing save.', blocks, existingBlocks);
      }
    }
  }, [blocks, onSaveBlocks, existingBlocks]); // Add existingBlocks here to re-evaluate save condition

  // Synchronize the internal active block index with the selected block index from parent
  useEffect(() => {
    if (selectedBlockIndex !== undefined && selectedBlockIndex !== null) {
      setActiveBlockIndex(selectedBlockIndex);
      setSelectedLogoRowIndex(0);
    }
  }, [selectedBlockIndex]);

  const parseNumericValue = (value: unknown): number => {
    const parsedValue: number = Number(value);
    if (!Number.isFinite(parsedValue)) {
      return 0;
    }
    return parsedValue;
  };

  const parseFrameValue = (value: unknown): number => {
    const parsedValue: number = parseNumericValue(value);
    return Math.max(0, Math.floor(parsedValue));
  };

  type BlockType = 'role-name' | 'single-cell' | 'song' | 'credits' | 'logo';

  const getBlockType = (block: Block | undefined): BlockType | 'unknown' => {
    if (!block) return 'unknown';
    if (block.isSongBlock) return 'song';
    if (block.isCreditsList) return 'credits';
    if (block.isLogoBlock) return 'logo';
    if (block.isSingleCell) return 'single-cell';
    return 'role-name';
  };

  const isPlainSingleCellBlock = (block: Block | undefined): boolean => {
    return getBlockType(block) === 'single-cell';
  };

  const handleBlockChange = (index: number, field: keyof Block, value: any) => {
    const blockForAnalytics: Block | undefined = blocks[index] ?? existingBlocks[index];
    trackEvent('block_property_changed', {
      project_id: selectedProject?.id,
      block_id: blockForAnalytics?.id,
      block_name: blockForAnalytics?.name,
      property_name: String(field),
      block_type: getBlockType(blockForAnalytics),
      is_cards_mode: isCardsMode,
    }, {
      dedupeKey: `${selectedProject?.id ?? 'no_project'}:${blockForAnalytics?.id ?? index}:${String(field)}`,
    });

    setBlocks(prevBlocks => {
      const cardSharedFields: Array<keyof Block> = [
        'cardFadeInFrames',
        'cardDurationFullyUpFrames',
        'cardFadeOutFrames',
        'cardSlugFrames',
        'cardOverrideFullWidth',
        'cardVerticalCenter',
      ];
      const isCardSharedField: boolean = cardSharedFields.includes(field);
      const selectedBlockId: string | undefined = prevBlocks[index]?.id;
      const activeCardGroup: CardGroup | undefined = selectedBlockId
        ? cardGroups.find((group: CardGroup) => group.blockIds.includes(selectedBlockId))
        : undefined;
      const cardBlockIdSet: Set<string> = new Set(activeCardGroup?.blockIds ?? []);

      const updatedBlocks = prevBlocks.map((block, i) => {
        const shouldUpdateCurrentBlock: boolean = i === index;
        const shouldUpdateCardBlock: boolean = isCardsMode && isCardSharedField && cardBlockIdSet.has(block.id);
        const shouldUpdate: boolean = shouldUpdateCurrentBlock || shouldUpdateCardBlock;

        if (shouldUpdate) {
          // Handle special case for hasSeparateValueFormatting
          const blockUsesForcedValueFormatting: boolean = isPlainSingleCellBlock(block);
          const blockHasSeparateValueFormatting: boolean = blockUsesForcedValueFormatting || block.hasSeparateValueFormatting;
          let newHasSeparateValueFormatting = blockUsesForcedValueFormatting || (
            field === 'hasSeparateValueFormatting'
              ? Boolean(value)
              : block.hasSeparateValueFormatting
          );

          // If we're disabling separate value formatting, copy label formatting to value formatting
          let newValueFontName = block.valueFontName;
          let newValueFontSize = block.valueFontSize;
          let newValueFontWeight = block.valueFontWeight;
          let newValueFontWidth = block.valueFontWidth;
          let newValueAlign = block.valueAlign;
          let newValueFontColor = block.valueFontColor;
          let newValueVerticalAlign = block.valueVerticalAlign;

          if (field === 'hasSeparateValueFormatting' && !value && !blockUsesForcedValueFormatting) {
            // Copy label formatting to value formatting
            newValueFontName = block.labelFontName;
            newValueFontSize = block.labelFontSize;
            newValueFontWeight = block.labelFontWeight;
            newValueFontWidth = block.labelFontWidth;
            newValueAlign = block.labelAlign;
            newValueFontColor = block.labelFontColor;
            newValueVerticalAlign = block.labelVerticalAlign;
          } else if (field === 'labelFontName' && !blockHasSeparateValueFormatting) {
            // If separate formatting is disabled, update value formatting when label formatting changes
            newValueFontName = String(value);
          } else if (field === 'labelFontSize' && !blockHasSeparateValueFormatting) {
            newValueFontSize = parseNumericValue(value);
          } else if (field === 'labelFontWeight' && !blockHasSeparateValueFormatting) {
            newValueFontWeight = parseNumericValue(value);
          } else if (field === 'labelFontWidth' && !blockHasSeparateValueFormatting) {
            newValueFontWidth = parseNumericValue(value);
          } else if (field === 'labelAlign' && !blockHasSeparateValueFormatting) {
            newValueAlign = String(value);
          } else if (field === 'labelFontColor' && !blockHasSeparateValueFormatting) {
            newValueFontColor = String(value);
          } else if (field === 'valueFontName') {
            newValueFontName = String(value);
          } else if (field === 'valueFontSize') {
            newValueFontSize = parseNumericValue(value);
          } else if (field === 'valueFontWeight') {
            newValueFontWeight = parseNumericValue(value);
          } else if (field === 'valueFontWidth') {
            newValueFontWidth = parseNumericValue(value);
          } else if (field === 'valueAlign') {
            newValueAlign = String(value);
          } else if (field === 'valueFontColor') {
            newValueFontColor = String(value);
          } else if (field === "valueVerticalAlign") {
            newValueVerticalAlign = String(value);
          }

          // Create a new Block instance for the modified block
          const newBlock = Block.create({
            ...block,
            name: field === 'name' ? String(value) : block.name,
            blockNameDisplayValue: field === 'blockNameDisplayValue' ? String(value) : block.blockNameDisplayValue,
            shouldDisplayBlockName: field === 'shouldDisplayBlockName' ? Boolean(value) : block.shouldDisplayBlockName,
            blockNameFont: field === 'blockNameFont' ? String(value) : block.blockNameFont,
            blockNameSize: field === 'blockNameSize' ? parseNumericValue(value) : block.blockNameSize,
            blockNameWeight: field === 'blockNameWeight' ? parseNumericValue(value) : block.blockNameWeight,
            blockNameFontWidth: field === 'blockNameFontWidth' ? parseNumericValue(value) : block.blockNameFontWidth,
            blockNameColor: field === 'blockNameColor' ? String(value) : block.blockNameColor,
            blockNameAlignment: field === 'blockNameAlignment' ? value as Block['blockNameAlignment'] : (block.blockNameAlignment ?? 'center'),
            blockNamePlacement: field === 'blockNamePlacement'
              ? Block.normalizeBlockNamePlacement(value)
              : (block.blockNamePlacement ?? 'vertical'),
            numColumns: field === 'numColumns' ? parseNumericValue(value) : block.numColumns,
            distanceBelowBlockName: field === 'distanceBelowBlockName' ? parseNumericValue(value) : block.distanceBelowBlockName,
            labelValueGap: field === 'labelValueGap' ? parseNumericValue(value) : block.labelValueGap,
            marginColumn: field === 'marginColumn' ? parseNumericValue(value) : (block.marginColumn ?? 45),
            edgeDistance: field === 'edgeDistance' ? parseNumericValue(value) : (block.edgeDistance ?? 0),
            distanceBetweenRows: field === 'distanceBetweenRows' ? parseNumericValue(value) : block.distanceBetweenRows,
            distanceFromPreviousBlock: field === 'distanceFromPreviousBlock' ? parseNumericValue(value) : block.distanceFromPreviousBlock,
            isSongBlock: field === 'isSongBlock' ? Boolean(value) : block.isSongBlock,
            isSingleCell: field === 'isSingleCell' ? Boolean(value) : block.isSingleCell,
            isCreditsList: field === 'isCreditsList' ? Boolean(value) : block.isCreditsList,
            separator: field === 'separator' ? String(value) : block.separator,
            isLogoBlock: field === 'isLogoBlock' ? Boolean(value) : block.isLogoBlock,
            sbsBlocks: field === 'sbsBlocks' ? String(value) as Block['sbsBlocks'] : (block.sbsBlocks ?? 'normal'),
            sbsDistance: field === 'sbsDistance' ? parseNumericValue(value) : block.sbsDistance,
            labelFontName: field === 'labelFontName' ? String(value) : block.labelFontName,
            labelFontSize: field === 'labelFontSize' ? parseNumericValue(value) : block.labelFontSize,
            labelFontWeight: field === 'labelFontWeight' ? parseNumericValue(value) : block.labelFontWeight,
            labelFontWidth: field === 'labelFontWidth' ? parseNumericValue(value) : block.labelFontWidth,
            labelAlign: field === 'labelAlign' ? String(value) : block.labelAlign,
            labelVerticalAlign: field === 'labelVerticalAlign' ? String(value) : block.labelVerticalAlign,
            labelFontColor: field === 'labelFontColor' ? String(value) : block.labelFontColor,
            valueFontName: newValueFontName,
            valueFontSize: newValueFontSize,
            valueFontWeight: newValueFontWeight,
            valueFontWidth: newValueFontWidth,
            valueAlign: newValueAlign,
            valueVerticalAlign: newValueVerticalAlign,
            valueFontColor: newValueFontColor,
            hasSeparateValueFormatting: newHasSeparateValueFormatting,
            labelValueAlignment: field === 'labelValueAlignment' ? String(value) as Block['labelValueAlignment'] : block.labelValueAlignment,
            ltrRtlControl: field === 'ltrRtlControl' ? Block.normalizeLtrRtlControl(value) : block.ltrRtlControl,
            exchangeLabelValue: field === 'exchangeLabelValue' ? Boolean(value) : block.exchangeLabelValue,
            cardFadeInFrames: field === 'cardFadeInFrames' ? parseFrameValue(value) : (block.cardFadeInFrames ?? 0),
            cardDurationFullyUpFrames: field === 'cardDurationFullyUpFrames' ? parseFrameValue(value) : (block.cardDurationFullyUpFrames ?? 0),
            cardFadeOutFrames: field === 'cardFadeOutFrames' ? parseFrameValue(value) : (block.cardFadeOutFrames ?? 0),
            cardSlugFrames: field === 'cardSlugFrames' ? parseFrameValue(value) : (block.cardSlugFrames ?? 0),
            cardOverrideFullWidth: field === 'cardOverrideFullWidth' ? Boolean(value) : (block.cardOverrideFullWidth ?? false),
            cardVerticalCenter: field === 'cardVerticalCenter' ? Boolean(value) : (block.cardVerticalCenter ?? false),
            cardHorizontalOffsetPx: field === 'cardHorizontalOffsetPx'
              ? Block.normalizeCardHorizontalOffsetPx(value)
              : (block.cardHorizontalOffsetPx ?? 0),
            isLocked: field === 'isLocked' ? Boolean(value) : block.isLocked,
            dataRows: block.dataRows // CRITICAL: Preserve dataRows
          });
          return newBlock;
        }
        return block;
      });
      console.log('BlockConfigForm: handleBlockChange, updatedBlocks:', updatedBlocks);
      return updatedBlocks;
    });
  };

  const handleLogoRowConfigChange = (field: LogoNumericConfigField, value: number): void => {
    setBlocks((previousBlocks: Block[]) => previousBlocks.map((block: Block, index: number) => {
      if (index !== activeBlockIndex || !block.dataRows[selectedLogoRowIndex]) {
        return block;
      }

      const updatedRows: RowData[] = [...block.dataRows];
      const currentRow: RowData = updatedRows[selectedLogoRowIndex];
      const currentConfig: LogoRowConfig = getLogoRowConfig(currentRow);
      const updatedConfig: LogoRowConfig = { ...currentConfig, [field]: value };

      return Block.create({
        ...block,
        dataRows: updatedRows.map((row: RowData, rowIndex: number) => (
          rowIndex === selectedLogoRowIndex
            ? { ...row, col2: JSON.stringify(updatedConfig) }
            : row
        )),
      });
    }));
  };

  const selectSettingsTab = (group: 'block' | 'card' | 'label' | 'value', tab: SettingsTab): void => {
    if (settingsTabs[group] === tab) {
      return;
    }
    trackEvent('block_config_section_toggled', {
      project_id: selectedProject?.id,
      section_name: `${group}_${tab}`,
      next_collapsed: tab === 'layout',
    });
    setSettingsTabs((previous) => ({ ...previous, [group]: tab }));
  };

  const toggleSettingsGroup = (group: 'block' | 'card' | 'label' | 'logo' | 'value'): void => {
    const nextCollapsed: boolean = !collapsedSettingsGroups[group];
    trackEvent('block_config_section_toggled', {
      project_id: selectedProject?.id,
      section_name: group,
      next_collapsed: nextCollapsed,
    });
    setCollapsedSettingsGroups((previous) => ({ ...previous, [group]: nextCollapsed }));
  };

  // Get the active block
  const activeBlock = blocks[activeBlockIndex];
  const hasBlockFontProperties: boolean = Boolean(
    !activeBlock?.isLogoBlock && activeBlock?.shouldDisplayBlockName
  );
  const blockSettingsTab: SettingsTab = hasBlockFontProperties ? settingsTabs.block : 'layout';
  const selectedLogoRow: RowData | undefined = activeBlock?.dataRows[selectedLogoRowIndex];
  const selectedLogoConfig: LogoRowConfig = getLogoRowConfig(selectedLogoRow);
  const activeBlockUsesForcedValueFormatting: boolean = isPlainSingleCellBlock(activeBlock);
  const shouldShowSeparateValueFormattingToggle: boolean = Boolean(activeBlock && !activeBlockUsesForcedValueFormatting);
  const shouldShowValueFormattingControls: boolean = Boolean(activeBlockUsesForcedValueFormatting || activeBlock?.hasSeparateValueFormatting);
  const fontOptions: FontOption[] = fontFamilies.flatMap((fontFamily: FontFamilyOption) => fontFamily.fonts);
  const getFontVariableConfig = (fontName: string | undefined): VariableFontConfig | undefined => {
    if (!fontName) {
      return undefined;
    }
    const variableFontConfig: VariableFontConfig | undefined =
      fontOptions.find((fontOption: FontOption) => fontOption.value === fontName)?.variableFontConfig;
    return variableFontConfig ? normalizeVariableFontConfigSteps(variableFontConfig) : undefined;
  };
  const handleBlockTypeChange = (index: number, blockType: BlockType) => {
    const blockForAnalytics: Block | undefined = blocks[index] ?? existingBlocks[index];
    trackEvent('block_type_changed', {
      project_id: selectedProject?.id,
      block_id: blockForAnalytics?.id,
      block_name: blockForAnalytics?.name,
      previous_type: getBlockType(blockForAnalytics),
      next_type: blockType,
    });
    setBlocks(prevBlocks => prevBlocks.map((block, i) => {
      if (i !== index) return block;
      const isSingleCellBlockType: boolean = blockType === 'single-cell';
      return Block.create({
        ...block,
        isSongBlock: blockType === 'song',
        isSingleCell: blockType === 'song' || blockType === 'credits' || isSingleCellBlockType,
        isCreditsList: blockType === 'credits',
        isLogoBlock: blockType === 'logo',
        hasSeparateValueFormatting: isSingleCellBlockType ? true : block.hasSeparateValueFormatting,
        dataRows: block.dataRows,
      });
    }));
  };


  const copyTargetBlocks: Block[] = blocks.filter((block: Block, index: number) => {
    if (index === activeBlockIndex) {
      return false;
    }
    const sourceType: BlockType | 'unknown' = getBlockType(activeBlock);
    const candidateType: BlockType | 'unknown' = getBlockType(block);
    if (sourceType === 'song' || sourceType === 'logo') {
      return candidateType === sourceType;
    }
    return candidateType !== 'song' && candidateType !== 'logo';
  });

  const openCopyDialog = (group: FormattingGroup): void => {
    setCopyFormattingGroup(group);
    setCopyTargetBlockIds(new Set<string>());
  };

  const closeCopyDialog = (): void => {
    setCopyFormattingGroup(null);
  };

  const toggleCopyTarget = (blockId: string): void => {
    if (copyTargetBlocks.find((block: Block) => block.id === blockId)?.isLocked) {
      return;
    }
    setCopyTargetBlockIds((previous: Set<string>) => {
      const next: Set<string> = new Set(previous);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  };

  const toggleCopyTargetAll = (): void => {
    const allIds: string[] = copyTargetBlocks
      .filter((block: Block) => !block.isLocked)
      .map((block: Block) => block.id);
    setCopyTargetBlockIds((previous: Set<string>) => {
      const allSelectableBlocksAreSelected: boolean = allIds.every((blockId: string) => previous.has(blockId));
      return allSelectableBlocksAreSelected ? new Set<string>() : new Set<string>(allIds);
    });
  };

  const applyCopyFormatting = (): void => {
    if (!copyFormattingGroup || copyTargetBlockIds.size === 0) {
      closeCopyDialog();
      return;
    }
    const sourceBlock: Block | undefined = blocks[activeBlockIndex];
    if (!sourceBlock) {
      closeCopyDialog();
      return;
    }
    const copiedFields: Partial<Block> = pickBlockFields(
      sourceBlock,
      COPY_FORMATTING_FIELDS[copyFormattingGroup]
    );

    setBlocks((prevBlocks: Block[]) => prevBlocks.map((block: Block) => {
      if (!copyTargetBlockIds.has(block.id) || block.id === sourceBlock.id || block.isLocked) {
        return block;
      }
      return Block.create({
        ...block,
        ...copiedFields,
        dataRows: block.dataRows,
      });
    }));
    closeCopyDialog();
  };

  const renderEssentials = () => (
    <div className="config-pane essentials-pane">
      <h3 className="pane-title">{t`Essentials`}</h3>
      <div className="pane-content">
        <div className="form-group">
          <label>{t`Block Type`}</label>
          <div className="input-wrapper">
            <Select
              className="dropdown-menu-select"
              value={activeBlock ? getBlockType(activeBlock) : 'role-name'}
              onChange={(e) => handleBlockTypeChange(activeBlockIndex, e.target.value as BlockType)}
              style={{ width: '130px', height: '34px' }}
            >
              <MenuItem value="role-name">{t`Role → Name`}</MenuItem>
              <MenuItem value="single-cell">{t`Single Cell`}</MenuItem>
              <MenuItem value="song">{t`Song`}</MenuItem>
              <MenuItem value="credits">{t`Credits List`}</MenuItem>
              <MenuItem value="logo">{t`Logo`}</MenuItem>
            </Select>
          </div>
        </div>

        {activeBlock && getBlockType(activeBlock) === 'credits' && (
          <div className="form-group">
            <label>{t`Separator`}</label>
            <div className="input-wrapper">
              <Select
                className="dropdown-menu-select"
                value={activeBlock.separator || '•'}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'separator', e.target.value as string)}
                style={{ width: '80px', height: '34px' }}
              >
                <MenuItem value="|">|</MenuItem>
                <MenuItem value="•">•</MenuItem>
                <MenuItem value=",">,</MenuItem>
                <MenuItem value="-">-</MenuItem>
                <MenuItem value="/">/</MenuItem>
              </Select>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>{t`Display Block Name`}</label>
          <div className="input-wrapper block-name-display-wrapper">
            <input
              type="checkbox"
              checked={activeBlock?.shouldDisplayBlockName}
              onChange={(e) => handleBlockChange(activeBlockIndex, 'shouldDisplayBlockName', e.target.checked)}
            />
            {activeBlock?.shouldDisplayBlockName && (
              <input
                type="text"
                value={activeBlock.blockNameDisplayValue ?? activeBlock.name}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'blockNameDisplayValue', e.target.value)}
                aria-label={t`Block name display value`}
              />
            )}
          </div>
        </div>

        <div className="form-group">
          <label>{t`Number of Columns`}</label>
          <div className="input-wrapper">
            <input
              type="number"
              min="1"
              value={activeBlock?.numColumns ?? 1}
              onChange={(e) => handleBlockChange(activeBlockIndex, 'numColumns', parseInt(e.target.value))}
            />
          </div>
        </div>

        {!activeBlock?.isLogoBlock && (!activeBlock?.isSingleCell || activeBlock?.isSongBlock) && (
          <div className="form-group">
            <label>{activeBlock?.isSongBlock ? t`Song Value Row Distance` : t`Label/Value Gap`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="0"
                value={activeBlock?.labelValueGap ?? 0}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueGap', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
        )}

        {!activeBlock?.isLogoBlock && !activeBlock?.isSongBlock && !activeBlock?.isSingleCell && !activeBlock?.isCreditsList && (
          <div className="form-group">
            <label>{t`Label/Value Alignment`}</label>
            <div className="input-wrapper">
              <Select
                className="dropdown-menu-select"
                value={activeBlock?.labelValueAlignment ?? 'horizontal'}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueAlignment', e.target.value)}
                style={{ height: '34px' }}
              >
                <MenuItem value="horizontal">{t`Horizontal`}</MenuItem>
                <MenuItem value="vertical">{t`Vertical`}</MenuItem>
                <MenuItem value="vertical_stacked">{t`Vertical Stacked`}</MenuItem>
              </Select>
            </div>
          </div>
        )}

        {activeBlock?.numColumns > 1 && !activeBlock?.isLogoBlock && (
          <div className="form-group">
            <label>{t`Distance Between Columns`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="0"
                value={activeBlock?.marginColumn ?? 45}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'marginColumn', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
        )}

        {!activeBlock?.isLogoBlock && (
          <div className="form-group">
            <label>{activeBlock?.isSongBlock ? t`Song Title/Details Distance` : t`Distance Between Rows`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="0"
                value={activeBlock?.distanceBetweenRows ?? 0}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBetweenRows', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
        )}

        {activeBlock?.isSingleCell && !activeBlock?.isLogoBlock && (
          <div className="form-group">
            <label>{t`Edge Distance`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="-20"
                max="80"
                value={activeBlock?.edgeDistance ?? 0}
                onChange={(e) => {
                  const parsedValue: number = Number.parseInt(e.target.value, 10);
                  handleBlockChange(activeBlockIndex, 'edgeDistance', Number.isNaN(parsedValue) ? 0 : parsedValue);
                }}
              />
              <span className="unit">px</span>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>{t`Distance from Previous Block`}</label>
          <div className="input-wrapper">
            <input
              type="number"
              min="0"
              value={activeBlock?.distanceFromPreviousBlock ?? 0}
              onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceFromPreviousBlock', parseInt(e.target.value))}
            />
            <span className="unit">px</span>
          </div>
        </div>
      </div>
    </div>
  );

  void renderEssentials;
  return (
    <>
      <h2 className="block-properties-title">
        <span>{t`Block Properties`}</span>
        <span className="block-properties-actions">
          {copyTargetBlocks.length > 0 && (
            <button
              type="button"
              className="copy-formatting-btn"
              aria-label={t`Copy block layout, label, and value formatting to other blocks`}
              title={t`Copy block layout, label, and value formatting to other blocks`}
              onClick={(e) => {
                e.stopPropagation();
                openCopyDialog('all');
              }}
            >
              <FormatPaintIcon fontSize="small" aria-hidden="true" />
            </button>
          )}
        </span>
      </h2>
      <div className="block-config-form">
        {isCardsMode && activeBlock && (
          <div className={`config-pane settings-group ${collapsedSettingsGroups.card ? 'collapsed' : ''}`} data-settings-tab="layout">
          <h3 className="pane-title" onClick={() => toggleSettingsGroup('card')}>
              {t`Card Settings`}
            </h3>
            <div className="pane-content">
              <div className="form-group fine-tune-control">
                <label>{t`Override to full width`}</label>
                <div className="input-wrapper">
                  <input
                    type="checkbox"
                    checked={activeBlock.cardOverrideFullWidth ?? false}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardOverrideFullWidth', e.target.checked)}
                  />
                </div>
              </div>
              <div className="form-group fine-tune-control">
                <label>{t`Vertical Center`}</label>
                <div className="input-wrapper">
                  <input
                    type="checkbox"
                    checked={activeBlock.cardVerticalCenter ?? false}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardVerticalCenter', e.target.checked)}
                  />
                </div>
              </div>
              <div className="form-group fine-tune-control">
                <label>{t`Horizontal Offset`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min={Block.CARD_HORIZONTAL_OFFSET_MIN_PX}
                    max={Block.CARD_HORIZONTAL_OFFSET_MAX_PX}
                    step="1"
                    value={activeBlock.cardHorizontalOffsetPx ?? 0}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardHorizontalOffsetPx', e.target.value)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Block Layout Properties */}
        <div className={`config-pane settings-group ${collapsedSettingsGroups.block ? 'collapsed' : ''}`} data-settings-tab={blockSettingsTab}>
          <h3 className="pane-title" onClick={() => toggleSettingsGroup('block')}>
            {t`Block Settings`}
            {copyTargetBlocks.length > 0 && (
              <button
                type="button"
                className="copy-formatting-btn"
                aria-label={t`Copy to Other Blocks`}
                title={t`Copy to Other Blocks`}
                onClick={(e) => {
                  e.stopPropagation();
                  openCopyDialog('layout');
                }}
              >
                <FormatPaintIcon fontSize="small" aria-hidden="true" />
              </button>
            )}
          </h3>
          {hasBlockFontProperties && (
            <div className="settings-tabs" role="tablist" aria-label={t`Block Settings`}>
              <button type="button" role="tab" aria-selected={blockSettingsTab === 'layout'} onClick={() => selectSettingsTab('block', 'layout')}>{t`Layout`}</button>
              <button type="button" role="tab" aria-selected={blockSettingsTab === 'font'} onClick={() => selectSettingsTab('block', 'font')}>{t`Font`}</button>
            </div>
          )}

          <div className="pane-content">
            <div className="form-group essentials-control">
              <label>{t`Block Type`}</label>
              <div className="input-wrapper">
                <Select
                  className="dropdown-menu-select"
                  value={activeBlock ? getBlockType(activeBlock) : 'role-name'}
                  onChange={(e) => handleBlockTypeChange(activeBlockIndex, e.target.value as BlockType)}
                  style={{ width: '130px', height: '34px' }}
                >
                  <MenuItem value="role-name">{t`Role → Name`}</MenuItem>
                  <MenuItem value="single-cell">{t`Single Cell`}</MenuItem>
                  <MenuItem value="song">{t`Song`}</MenuItem>
                  <MenuItem value="credits">{t`Credits List`}</MenuItem>
                  <MenuItem value="logo">{t`Logo`}</MenuItem>
                </Select>
              </div>
            </div>
            {activeBlock && getBlockType(activeBlock) === 'credits' && (
              <div className="form-group essentials-control">
                <label>{t`Separator`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.separator || '•'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'separator', e.target.value as string)}
                    style={{ width: '80px', height: '34px' }}
                  >
                    <MenuItem value="|">|</MenuItem>
                    <MenuItem value="•">•</MenuItem>
                    <MenuItem value=",">,</MenuItem>
                    <MenuItem value="-">-</MenuItem>
                    <MenuItem value="/">/</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            <div className="form-group essentials-control">
              <label>{t`Display Block Name`}</label>
              <div className="input-wrapper block-name-display-wrapper">
                <input
                  type="checkbox"
                  checked={activeBlock?.shouldDisplayBlockName}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'shouldDisplayBlockName', e.target.checked)}
                />
                {activeBlock?.shouldDisplayBlockName && (
                  <input
                    type="text"
                    value={activeBlock.blockNameDisplayValue ?? activeBlock.name}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'blockNameDisplayValue', e.target.value)}
                    aria-label={t`Block name display value`}
                  />
                )}
              </div>
            </div>
            {activeBlock?.shouldDisplayBlockName && !activeBlock.isSongBlock && (
              <div className="form-group fine-tune-control">
                <label>{t`Block Name Placement`}</label>
                <div className="input-wrapper">
                  <IconToggleGroup
                    ariaLabel={t`Block Name Placement`}
                    value={activeBlock.blockNamePlacement ?? 'vertical'}
                    onChange={(value) => handleBlockChange(activeBlockIndex, 'blockNamePlacement', value)}
                    options={[
                      {
                        value: 'horizontal_left',
                        label: t`Horizontal Left`,
                        icon: <BlockNamePlacementHorizontalLeftIcon fontSize="small" />,
                      },
                      {
                        value: 'vertical',
                        label: t`Vertical`,
                        icon: <BlockNamePlacementVerticalIcon fontSize="small" />,
                      },
                      {
                        value: 'horizontal_right',
                        label: t`Horizontal Right`,
                        icon: <BlockNamePlacementHorizontalRightIcon fontSize="small" />,
                      },
                    ]}
                  />
                </div>
              </div>
            )}
            <Collapse
              className="block-name-alignment-collapse"
              in={activeBlock?.shouldDisplayBlockName && (activeBlock?.blockNamePlacement ?? 'vertical') === 'vertical'}
              timeout={140}
              easing={{ enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
            >
              <div className="form-group fine-tune-control">
                <label>{t`Block Name Alignment`}</label>
                <div className="input-wrapper">
                  <IconToggleGroup
                    ariaLabel={t`Block Name Alignment`}
                    value={activeBlock?.blockNameAlignment ?? 'center'}
                    onChange={(value) => handleBlockChange(activeBlockIndex, 'blockNameAlignment', value)}
                    options={[
                      { value: 'left', label: t`Left`, icon: <AlignHorizontalLeftIcon fontSize="small" /> },
                      { value: 'center', label: t`Center`, icon: <AlignHorizontalCenterIcon fontSize="small" /> },
                      { value: 'right', label: t`Right`, icon: <AlignHorizontalRightIcon fontSize="small" /> },
                    ]}
                  />
                </div>
              </div>
            </Collapse>
            {/* block name font */}
            {activeBlock?.shouldDisplayBlockName && (
              <div className="fine-tune-control font-control">
                <div className="form-group">
                  <label>{t`Block Name Font`}</label>
                  <div className="input-wrapper">
                    <ReusableFontPicker
                      value={activeBlock?.blockNameFont}
                      fontFamilies={fontFamilies}
                      onChange={(nextFont: string) =>
                        handleBlockChange(activeBlockIndex, "blockNameFont", nextFont)
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t`Block Name Font Size`}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      min="6"
                      max="72"
                      value={activeBlock?.blockNameSize}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'blockNameSize', parseInt(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                <FontWeightWidthControls
                  fontName={activeBlock?.blockNameFont}
                  weight={activeBlock?.blockNameWeight}
                  width={activeBlock?.blockNameFontWidth}
                  weightField="blockNameWeight"
                  widthField="blockNameFontWidth"
                  onChange={(field, nextValue) => handleBlockChange(activeBlockIndex, field, nextValue)}
                  weightLabel={t`Block Name Font Weight`}
                  widthLabel={t`Width`}
                  variableFontConfig={getFontVariableConfig(activeBlock?.blockNameFont)}
                  fontOptions={fontOptions}
                  onFontChange={(nextFont) => handleBlockChange(activeBlockIndex, 'blockNameFont', nextFont)}
                />

                <div className="form-group">
                  <label>{t`Block Name Font Color`}</label>
                  <div className="input-wrapper color-input-wrapper">
                    <ColorPicker
                      value={activeBlock?.blockNameColor ?? '#ffffff'}
                      onChange={(color) => handleBlockChange(activeBlockIndex, 'blockNameColor', color)}
                      showValue
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="form-group essentials-control">
              <label>{t`Number of Columns`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="1"
                  value={activeBlock?.numColumns}
                  aria-label={t`Number of Columns`}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'numColumns', parseInt(e.target.value))}
                />
              </div>
            </div>
            {!activeBlock?.isLogoBlock && (
              <div className="form-group fine-tune-control">
                <label>{t`Side-by-Side Block`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.sbsBlocks ?? 'normal'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'sbsBlocks', e.target.value)}
                    MenuProps={{
                      PaperProps: { className: "block-font-menu" }
                    }}
                  >
                    <MenuItem value="normal">{t`Normal`}</MenuItem>
                    <MenuItem value="left">{t`Left`}</MenuItem>
                    <MenuItem value="right">{t`Right`}</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            {activeBlock?.sbsBlocks === 'left' && (
              <div className="form-group fine-tune-control">
                <label>{t`Side by Side Distance`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    value={activeBlock?.sbsDistance ?? 0}
                    onChange={(e) =>
                      handleBlockChange(activeBlockIndex, 'sbsDistance', parseInt(e.target.value))
                    }
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}
            {activeBlock?.shouldDisplayBlockName && (
            <div className="form-group fine-tune-control">
              <label>
                {!activeBlock.isSongBlock &&
                (activeBlock.blockNamePlacement === 'horizontal_left' ||
                  activeBlock.blockNamePlacement === 'horizontal_right')
                  ? t`Distance From Block Name`
                  : t`Distance Below Block Name`}
              </label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.distanceBelowBlockName}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBelowBlockName', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>
            )}
            {!activeBlock?.isLogoBlock && (!activeBlock?.isSingleCell || activeBlock?.isSongBlock) && (
              <>
                <div className="form-group fine-tune-control">
                  <label>{t`Exchange Label/Value`}</label>
                  <div className="input-wrapper">
                    <input
                      type="checkbox"
                      checked={activeBlock?.exchangeLabelValue ?? false}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'exchangeLabelValue', e.target.checked)}
                    />
                  </div>
                </div>
                <div className="form-group essentials-control">
                  <label>{ activeBlock?.isSongBlock ? t`Song Value Row Distance` : t`Label/Value Gap`}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      min="0"
                      value={activeBlock?.labelValueGap}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueGap', parseInt(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>
              </>
            )}
            {!activeBlock?.isLogoBlock && !activeBlock?.isSongBlock && !activeBlock?.isSingleCell && !activeBlock?.isCreditsList && (
              <div className="form-group essentials-control">
                <label>{t`Label/Value Alignment`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.labelValueAlignment ?? 'horizontal'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueAlignment', e.target.value)}
                    style={{ height: '34px' }}
                  >
                    <MenuItem value="horizontal">{t`Horizontal`}</MenuItem>
                    <MenuItem value="vertical">{t`Vertical`}</MenuItem>
                    <MenuItem value="vertical_stacked">{t`Vertical Stacked`}</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            {!activeBlock?.isLogoBlock && (
              <div className="form-group fine-tune-control">
                <label>{t`LTR/RTL Control`}</label>
                <div className="input-wrapper">
                  <IconToggleGroup
                    ariaLabel={t`LTR/RTL Control`}
                    value={activeBlock?.ltrRtlControl ?? 'auto'}
                    onChange={(value) => handleBlockChange(activeBlockIndex, 'ltrRtlControl', value)}
                    options={[
                      { value: 'ltr', label: t`LTR`, icon: <FormatTextdirectionLToRIcon fontSize="small" /> },
                      { value: 'rtl', label: t`RTL`, icon: <FormatTextdirectionRToLIcon fontSize="small" /> },
                      { value: 'auto', label: t`Auto`, icon: <HdrAutoIcon fontSize="small" /> },
                    ]}
                  />
                </div>
              </div>
            )}
            {activeBlock?.numColumns > 1 && !activeBlock?.isLogoBlock && (
              <div className="form-group essentials-control">
                <label>{t`Distance Between Columns`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    value={activeBlock?.marginColumn ?? 45}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'marginColumn', parseInt(e.target.value))}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}

            {!activeBlock?.isLogoBlock && (
              <div className="form-group essentials-control">
                <label>{ activeBlock?.isSongBlock ? t`Song Title/Details Distance` : t`Distance Between Rows`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    value={activeBlock?.distanceBetweenRows}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBetweenRows', parseInt(e.target.value))}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}
            {activeBlock?.isSingleCell && !activeBlock?.isLogoBlock && (
              <div className="form-group essentials-control">
                <label>{t`Edge Distance`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="-20"
                    max="80"
                    value={activeBlock?.edgeDistance ?? 0}
                    onChange={(e) => {
                      const parsedValue: number = Number.parseInt(e.target.value, 10);
                      handleBlockChange(
                        activeBlockIndex,
                        'edgeDistance',
                        Number.isNaN(parsedValue) ? 0 : parsedValue
                      );
                    }}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}
            <div className="form-group essentials-control">
              <label>{t`Distance from Previous Block`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.distanceFromPreviousBlock}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceFromPreviousBlock', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>

          </div>
        </div>

        {activeBlock?.isLogoBlock && (
          <div className={`config-pane settings-group logo-settings ${collapsedSettingsGroups.logo ? 'collapsed' : ''}`}>
            <h3 className="pane-title" onClick={() => toggleSettingsGroup('logo')}>
              {t`Logo Configuration`}
            </h3>
            <div className="pane-content">
              <div className="block-logo-settings">
                <div className="block-logo-settings-section">
                  <div className="block-logo-settings-title">{t`Logo Options`}</div>
                  {activeBlock.dataRows.length > 0 ? (
                    <>
                      <div className="form-group">
                        <label>{t`Select Logo`}</label>
                        <div className="input-wrapper">
                          <Select
                            className="dropdown-menu-select"
                            value={selectedLogoRowIndex}
                            onChange={(event) => setSelectedLogoRowIndex(Number(event.target.value))}
                          >
                            {activeBlock.dataRows.map((row: RowData, index: number) => (
                              <MenuItem key={index} value={index}>
                                {String(row.col1 || `Logo ${index + 1}`)}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>{t`Scale`}</label>
                        <div className="input-wrapper">
                          <input
                            type="number"
                            min="1"
                            max="400"
                            step="1"
                            value={selectedLogoConfig.scale}
                            onChange={(event) => {
                              const parsedValue: number = Number.parseInt(event.target.value, 10);
                              handleLogoRowConfigChange('scale', Number.isNaN(parsedValue) ? 100 : parsedValue);
                            }}
                          />
                          <span className="unit">%</span>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>{t`X Offset`}</label>
                        <div className="input-wrapper">
                          <input
                            type="number"
                            value={selectedLogoConfig.xOffset}
                            onChange={(event) => {
                              const parsedValue: number = Number.parseInt(event.target.value, 10);
                              handleLogoRowConfigChange('xOffset', Number.isNaN(parsedValue) ? 0 : parsedValue);
                            }}
                          />
                          <span className="unit">px</span>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>{t`Y Offset`}</label>
                        <div className="input-wrapper">
                          <input
                            type="number"
                            value={selectedLogoConfig.yOffset}
                            onChange={(event) => {
                              const parsedValue: number = Number.parseInt(event.target.value, 10);
                              handleLogoRowConfigChange('yOffset', Number.isNaN(parsedValue) ? 0 : parsedValue);
                            }}
                          />
                          <span className="unit">px</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="block-logo-settings-empty">{t`No logo rows available. Add data to configure logos.`}</p>
                  )}
                </div>
                <div className="block-logo-settings-section">
                  <div className="block-logo-settings-title">{t`Block Layout`}</div>
                  <div className="form-group">
                    <label>{t`Logo Distance`}</label>
                    <div className="input-wrapper">
                      <input
                        type="number"
                        min="0"
                        value={activeBlock.marginColumn ?? 45}
                        onChange={(event) => handleBlockChange(activeBlockIndex, 'marginColumn', event.target.value)}
                      />
                      <span className="unit">px</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t`Distance Between Rows`}</label>
                    <div className="input-wrapper">
                      <input
                        type="number"
                        min="0"
                        value={activeBlock.distanceBetweenRows}
                        onChange={(event) => handleBlockChange(activeBlockIndex, 'distanceBetweenRows', event.target.value)}
                      />
                      <span className="unit">px</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Label Formatting (when NOT Logo Block or plain Single Cell or Song Block or Credits List) */}
        {!(activeBlock?.isLogoBlock || activeBlock?.isSingleCell || activeBlock?.isCreditsList || activeBlock?.isSongBlock) && (
            <div className={`config-pane settings-group label-formatting ${collapsedSettingsGroups.label ? 'collapsed' : ''}`} data-settings-tab={settingsTabs.label}>
              <h3 className="pane-title" onClick={() => toggleSettingsGroup('label')}>
                {t`Label Formatting`}
                {copyTargetBlocks.length > 0 && (
                  <button
                    type="button"
                    className="copy-formatting-btn"
                    aria-label={t`Copy to Other Blocks`}
                    title={t`Copy to Other Blocks`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCopyDialog('label');
                    }}
                  >
                    <FormatPaintIcon fontSize="small" aria-hidden="true" />
                  </button>
                )}
              </h3>
              <div className="settings-tabs" role="tablist" aria-label={t`Label Formatting`}>
                <button type="button" role="tab" aria-selected={settingsTabs.label === 'layout'} onClick={() => selectSettingsTab('label', 'layout')}>{t`Layout`}</button>
                <button type="button" role="tab" aria-selected={settingsTabs.label === 'font'} onClick={() => selectSettingsTab('label', 'font')}>{t`Font`}</button>
              </div>
              <div className="pane-content">
                <div className="form-group essentials-control font-control">
                  <label>{t`Font`}</label>
                  <div className="input-wrapper">
                    <ReusableFontPicker
                      value={activeBlock?.labelFontName}
                      fontFamilies={fontFamilies}
                      onChange={(nextFont: string) =>
                        handleBlockChange(activeBlockIndex, "labelFontName", nextFont)
                      }
                    />
                  </div>
                </div>

                <div className="form-group essentials-control font-control">
                  <label>{t`Font Size`}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      min="6"
                      max="72"
                      value={activeBlock?.labelFontSize}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'labelFontSize', parseInt(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                <div className="fine-tune-control font-control">
                  <FontWeightWidthControls
                    fontName={activeBlock?.labelFontName}
                    weight={activeBlock?.labelFontWeight}
                    width={activeBlock?.labelFontWidth}
                    weightField="labelFontWeight"
                    widthField="labelFontWidth"
                    onChange={(field, nextValue) => handleBlockChange(activeBlockIndex, field, nextValue)}
                    weightLabel={t`Font Weight`}
                    widthLabel={t`Width`}
                    variableFontConfig={getFontVariableConfig(activeBlock?.labelFontName)}
                    fontOptions={fontOptions}
                    onFontChange={(nextFont) => handleBlockChange(activeBlockIndex, 'labelFontName', nextFont)}
                  />
                </div>

                <div className="form-group fine-tune-control">
                  <label>{t`Alignment`}</label>
                  <div className="input-wrapper">
                    <IconToggleGroup
                      ariaLabel={t`Alignment`}
                      value={activeBlock?.labelAlign ?? 'right'}
                      onChange={(value) => handleBlockChange(activeBlockIndex, 'labelAlign', value)}
                      options={[
                        { value: 'left', label: t`Left`, icon: <AlignHorizontalLeftIcon fontSize="small" /> },
                        { value: 'center', label: t`Center`, icon: <AlignHorizontalCenterIcon fontSize="small" /> },
                        { value: 'right', label: t`Right`, icon: <AlignHorizontalRightIcon fontSize="small" /> },
                      ]}
                    />
                  </div>
                </div>

                <div className="form-group fine-tune-control">
                  <label>{t`Vertical Align`}</label>
                  <div className="input-wrapper">
                    <IconToggleGroup
                      ariaLabel={t`Vertical Align`}
                      value={activeBlock?.labelVerticalAlign ?? 'center'}
                      onChange={(value) => handleBlockChange(activeBlockIndex, 'labelVerticalAlign', value)}
                      options={[
                        { value: 'top', label: t`Top`, icon: <AlignVerticalTopIcon fontSize="small" /> },
                        { value: 'center', label: t`Center`, icon: <AlignVerticalCenterIcon fontSize="small" /> },
                        { value: 'bottom', label: t`Bottom`, icon: <AlignVerticalBottomIcon fontSize="small" /> },
                      ]}
                    />
                  </div>
                </div>

                <div className="form-group essentials-control font-control">
                  <label>{t`Font Color`}</label>
                  <div className="input-wrapper color-input-wrapper">
                    <ColorPicker
                      value={activeBlock?.labelFontColor ?? '#ffffff'}
                      onChange={(color) => handleBlockChange(activeBlockIndex, 'labelFontColor', color)}
                      showValue
                    />
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Value Formatting Properties (when NOT Logo Block) */}
        {!activeBlock?.isLogoBlock && (
            <div className={`config-pane settings-group value-formatting ${collapsedSettingsGroups.value ? 'collapsed' : ''}`} data-settings-tab={settingsTabs.value}>
              <h3 className="pane-title" onClick={() => toggleSettingsGroup('value')}>
                {t`Value Formatting`}
                {copyTargetBlocks.length > 0 && (
                  <button
                    type="button"
                    className="copy-formatting-btn"
                    aria-label={t`Copy to Other Blocks`}
                    title={t`Copy to Other Blocks`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCopyDialog('value');
                    }}
                  >
                    <FormatPaintIcon fontSize="small" aria-hidden="true" />
                  </button>
                )}
              </h3>
              <div className="settings-tabs" role="tablist" aria-label={t`Value Formatting`}>
                <button type="button" role="tab" aria-selected={settingsTabs.value === 'layout'} onClick={() => selectSettingsTab('value', 'layout')}>{t`Layout`}</button>
                <button type="button" role="tab" aria-selected={settingsTabs.value === 'font'} onClick={() => selectSettingsTab('value', 'font')}>{t`Font`}</button>
              </div>
              <div className="pane-content">
                {/* Toggle for separate value formatting */}
                {shouldShowSeparateValueFormattingToggle && (
                  <div className="form-group font-control" style={{ marginLeft: "-11px", display: "flex", flexDirection: "row", alignItems: "center", whiteSpace: "nowrap", }}>
                    <label style={{ marginLeft: "12px" }}>{t`Separate formatting for values?`}</label>
                    <input
                      type="checkbox"
                      style={{ marginLeft: "8px" }}
                      checked={activeBlock?.hasSeparateValueFormatting}
                      onChange={(e) =>
                        handleBlockChange(activeBlockIndex, 'hasSeparateValueFormatting', e.target.checked)
                      }
                    />
                  </div>
                )}

                {/* Value formatting options - only show if separate formatting is enabled */}
                {shouldShowValueFormattingControls && (
                  <>
                    <div className="form-group essentials-control font-control">
                      <label>{t`Font`}</label>
                      <div className="input-wrapper">
                        <ReusableFontPicker
                          value={activeBlock?.valueFontName}
                          fontFamilies={fontFamilies}
                          onChange={(nextFont: string) =>
                            handleBlockChange(activeBlockIndex, "valueFontName", nextFont)
                          }
                        />
                      </div>
                    </div>

                    <div className="form-group essentials-control font-control">
                      <label>{t`Font Size`}</label>
                      <div className="input-wrapper">
                        <input
                          type="number"
                          min="6"
                          max="72"
                          value={activeBlock?.valueFontSize}
                          onChange={(e) => handleBlockChange(activeBlockIndex, 'valueFontSize', parseInt(e.target.value))}
                        />
                        <span className="unit">px</span>
                      </div>
                    </div>

                    <div className="fine-tune-control font-control">
                      <FontWeightWidthControls
                        fontName={activeBlock?.valueFontName}
                        weight={activeBlock?.valueFontWeight}
                        width={activeBlock?.valueFontWidth}
                        weightField="valueFontWeight"
                        widthField="valueFontWidth"
                        onChange={(field, nextValue) => handleBlockChange(activeBlockIndex, field, nextValue)}
                        weightLabel={t`Font Weight`}
                        widthLabel={t`Width`}
                        variableFontConfig={getFontVariableConfig(activeBlock?.valueFontName)}
                        fontOptions={fontOptions}
                        onFontChange={(nextFont) => handleBlockChange(activeBlockIndex, 'valueFontName', nextFont)}
                      />
                    </div>

                    <div className="form-group fine-tune-control">
                      <label>{t`Alignment`}</label>
                      <div className="input-wrapper">
                        <IconToggleGroup
                          ariaLabel={t`Alignment`}
                          value={activeBlock?.valueAlign ?? 'right'}
                          onChange={(value) => handleBlockChange(activeBlockIndex, 'valueAlign', value)}
                          options={[
                            { value: 'left', label: t`Left`, icon: <AlignHorizontalLeftIcon fontSize="small" /> },
                            { value: 'center', label: t`Center`, icon: <AlignHorizontalCenterIcon fontSize="small" /> },
                            { value: 'right', label: t`Right`, icon: <AlignHorizontalRightIcon fontSize="small" /> },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="form-group fine-tune-control">
                      <label>{t`Vertical Align`}</label>
                      <div className="input-wrapper">
                        <IconToggleGroup
                          ariaLabel={t`Vertical Align`}
                          value={activeBlock?.valueVerticalAlign ?? 'center'}
                          onChange={(value) => handleBlockChange(activeBlockIndex, 'valueVerticalAlign', value)}
                          options={[
                            { value: 'top', label: t`Top`, icon: <AlignVerticalTopIcon fontSize="small" /> },
                            { value: 'center', label: t`Center`, icon: <AlignVerticalCenterIcon fontSize="small" /> },
                            { value: 'bottom', label: t`Bottom`, icon: <AlignVerticalBottomIcon fontSize="small" /> },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="form-group essentials-control font-control">
                      <label>{t`Font Color`}</label>
                      <div className="input-wrapper color-input-wrapper">
                        <ColorPicker
                          value={activeBlock?.valueFontColor ?? '#ffffff'}
                          onChange={(color) => handleBlockChange(activeBlockIndex, 'valueFontColor', color)}
                          showValue
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
        )}
      </div>

      <CopyFormattingDialog
        group={copyFormattingGroup}
        sourceBlockName={activeBlock?.name ?? ''}
        targetBlocks={copyTargetBlocks}
        selectedBlockIds={copyTargetBlockIds}
        onToggleBlock={toggleCopyTarget}
        onToggleAll={toggleCopyTargetAll}
        onClose={closeCopyDialog}
        onApply={applyCopyFormatting}
      />
    </>
  );
};

export default BlockConfigForm;
